/**
 * Hub theme preference — shared across dashboard / paper / live.
 *
 * Default: system `prefers-color-scheme` only when nothing is stored.
 * After first toggle, choice is locked in localStorage (`pmhub.theme`)
 * and a cookie with Domain=.lunes-crypto.ru so subdomains share one preference.
 * Apex moon landing is out of scope — this module is SPA-only.
 */

export const THEME_KEY = 'pmhub.theme'
export const THEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1y

/** @returns {string} */
function cookieDomainAttr() {
  try {
    const host = window.location.hostname
    if (host === 'lunes-crypto.ru' || host.endsWith('.lunes-crypto.ru')) {
      return '; Domain=.lunes-crypto.ru'
    }
  } catch {
    /* ignore */
  }
  return ''
}

/** @returns {'light'|'dark'|null} */
export function readThemeCookie() {
  try {
    const raw = document.cookie || ''
    const parts = raw.split(';')
    for (const part of parts) {
      const eq = part.indexOf('=')
      if (eq < 0) continue
      const name = part.slice(0, eq).trim()
      if (name !== THEME_KEY) continue
      const value = part.slice(eq + 1).trim()
      if (value === 'dark' || value === 'light') return value
    }
  } catch {
    /* ignore */
  }
  return null
}

/** @param {'light'|'dark'} theme */
export function writeThemeCookie(theme) {
  const t = theme === 'dark' ? 'dark' : 'light'
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie =
      `${THEME_KEY}=${t}; Path=/; Max-Age=${THEME_COOKIE_MAX_AGE}; SameSite=Lax` +
      cookieDomainAttr() +
      secure
  } catch {
    /* ignore */
  }
}

/** Persist to localStorage + shared cookie (no DOM apply). @param {'light'|'dark'} theme */
function persistTheme(theme) {
  const t = theme === 'dark' ? 'dark' : 'light'
  try {
    localStorage.setItem(THEME_KEY, t)
  } catch {
    /* ignore */
  }
  writeThemeCookie(t)
  return t
}

/** @returns {'light'|'dark'} */
export function resolveTheme() {
  // Cookie first — shared across hub/paper/live subdomains.
  const fromCookie = readThemeCookie()
  if (fromCookie === 'dark' || fromCookie === 'light') return fromCookie

  try {
    const stored = localStorage.getItem(THEME_KEY)
    if (stored === 'dark' || stored === 'light') return stored
  } catch {
    /* ignore */
  }
  try {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }
  } catch {
    /* ignore */
  }
  return 'light'
}

/** @param {'light'|'dark'} theme */
export function applyTheme(theme) {
  const t = theme === 'dark' ? 'dark' : 'light'
  const root = document.documentElement
  root.setAttribute('data-theme', t)
  root.style.colorScheme = t

  const themeColor = document.querySelector('meta[name="theme-color"]')
  if (themeColor) themeColor.setAttribute('content', t === 'dark' ? '#0e1116' : '#ffffff')

  const colorScheme = document.querySelector('meta[name="color-scheme"]')
  if (colorScheme) colorScheme.setAttribute('content', t)
}

/** @param {'light'|'dark'} theme */
export function setTheme(theme) {
  const t = persistTheme(theme)
  applyTheme(t)
  return t
}

/** @returns {'light'|'dark'} */
export function toggleTheme() {
  const next = resolveTheme() === 'dark' ? 'light' : 'dark'
  return setTheme(next)
}

/**
 * Apply stored / system theme before first paint of SPA chrome.
 * If either cookie or localStorage already has a choice, re-persist both
 * so a host-local LS preference migrates into the shared cookie.
 */
export function initTheme() {
  const t = resolveTheme()
  applyTheme(t)
  try {
    const cookie = readThemeCookie()
    let ls = null
    try {
      ls = localStorage.getItem(THEME_KEY)
    } catch {
      /* ignore */
    }
    if (
      cookie === 'dark' ||
      cookie === 'light' ||
      ls === 'dark' ||
      ls === 'light'
    ) {
      persistTheme(t)
    }
  } catch {
    /* ignore */
  }
}
